// src/components/Profile/AvatarSelector.jsx

import React, { useState, useRef } from 'react';
import { FaUser, FaTimes, FaCamera, FaUpload } from 'react-icons/fa';
import { avatarMapping } from '../Hooks/avatarMapping';
import useApi from '../../../Hooks/useApi';
import './css/avatarSelector.css';

const convertToWebp = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (event) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        // Resize to max 300x300 for profile images
        const maxSize = 300;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              const webpFile = new File(
                [blob],
                file.name.replace(/\.[^.]+$/, '.webp'),
                { type: 'image/webp' }
              );
              resolve(webpFile);
            } else {
              reject(new Error('Failed to convert image to webp'));
            }
          },
          'image/webp',
          0.9
        );
      };
      img.src = event.target.result;
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });

const AvatarSelector = ({ selectedAvatarId, onAvatarSelect, customImageUrl, onCustomImageUpload, translations }) => {
  const [showModal, setShowModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);
  const api = useApi();

  const handleAvatarSelect = (avatarKey) => {
    onAvatarSelect(avatarKey);
    setShowModal(false);
  };

  const getCurrentAvatar = () => {
    // If there's a custom image URL, use that first
    if (customImageUrl) {
      return customImageUrl;
    }
    // Otherwise check for predefined avatar
    if (selectedAvatarId && avatarMapping[selectedAvatarId]) {
      return avatarMapping[selectedAvatarId];
    }
    return null;
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError(translations?.invalidFileType || 'Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError(translations?.fileTooLarge || 'File size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const webpFile = await convertToWebp(file);
      const formData = new FormData();
      formData.append('image', webpFile);

      const response = await api.post(
        `${window.baseDomain}api/account/profile-image`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response?.imageUrl) {
        // Clear any predefined avatar selection
        onAvatarSelect('');
        // Set the custom image URL
        if (onCustomImageUpload) {
          onCustomImageUpload(response.imageUrl);
        }
        setShowModal(false);
      }
    } catch (error) {
      console.error('Error uploading profile image:', error);
      setUploadError(translations?.uploadError || 'Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <>
      {/* Avatar Display */}
      <div className="profile-page">
        <div className="avatar-selector-section">
          <div
            className="avatar-selector-container"
            onClick={() => setShowModal(true)}
            role="button"
            tabIndex={0}
            onKeyPress={(e) => handleKeyPress(e, () => setShowModal(true))}
            aria-label="Select avatar"
          >
            {getCurrentAvatar() ? (
              <>
                <img
                  src={getCurrentAvatar()}
                  alt="Selected avatar"
                  className="avatar-selector-image"
                />
                <div className="avatar-selector-overlay">
                  <FaCamera className="avatar-selector-overlay-icon" />
                </div>
              </>
            ) : (
              <div className="avatar-selector-placeholder">
                <FaUser className="avatar-selector-placeholder-icon" />
                <span className="avatar-selector-placeholder-text">
                  {translations?.selectAvatar || 'Click to select avatar'}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Avatar Selection Modal */}
      {showModal && (
        <div className="profile-page">
          <div className="avatar-selector-modal" role="dialog" aria-modal="true">
            <div className="avatar-selector-modal-content">
              <div className="avatar-selector-modal-header">
                <h3>{translations?.chooseAvatar || 'Choose an Avatar'}</h3>
                <button
                  className="avatar-selector-modal-close"
                  onClick={() => setShowModal(false)}
                  aria-label="Close modal"
                >
                  <FaTimes />
                </button>
              </div>

              {/* Custom Upload Section */}
              <div className="avatar-upload-section">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                <button
                  className="avatar-upload-button"
                  onClick={triggerFileInput}
                  disabled={isUploading}
                >
                  <FaUpload className="avatar-upload-icon" />
                  <span>
                    {isUploading
                      ? (translations?.uploading || 'Uploading...')
                      : (translations?.uploadCustom || 'Upload Custom Image')
                    }
                  </span>
                </button>
                {uploadError && (
                  <p className="avatar-upload-error">{uploadError}</p>
                )}
              </div>

              <div className="avatar-selector-divider">
                <span>{translations?.orChooseAvatar || 'or choose an avatar'}</span>
              </div>

              <div className="avatar-selector-grid">
                {Object.keys(avatarMapping).map((key, index) => (
                  <div
                    key={key}
                    className={`avatar-selector-option-wrapper ${selectedAvatarId === key && !customImageUrl ? 'avatar-selector-selected' : ''}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <img
                      src={avatarMapping[key]}
                      alt={`Avatar ${key}`}
                      className="avatar-selector-option"
                      onClick={() => handleAvatarSelect(key)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => handleKeyPress(e, () => handleAvatarSelect(key))}
                      aria-label={`Select avatar ${key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AvatarSelector;